import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Modal, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash, faUser, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { AppAuthStateProps } from '../../types/propTypes';
import { getUserEmail, getUserRole } from '../../utils/user';
import { callQuery } from '../../amplify/api-gateway/query';
import { callWrite } from '../../amplify/api-gateway/write';
import { useGlobalOptions } from '../common/GlobalOptionsContext';
import { Link } from 'react-router-dom';
import './ProfilePage.scss';

interface UserDataSummary {
    username: string;
    email?: string;
    role?: string;
    notes_count: number;
    chats_count: number;
    favorites_count: number;
    favorite_lists_count: number;
    llm_feedback_count: number;
    followed_patients_count: number;
    user_events_count: number;
}

export const ProfilePage = (props: AppAuthStateProps) => {
    const [userDataSummary, setUserDataSummary] = useState<UserDataSummary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const email = getUserEmail(props.user);
    const role = getUserRole(props.user);
    const { getDerivedRole } = useGlobalOptions();
    const derivedRole = getDerivedRole(role);

    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const summary = await callQuery({
                operation: 'get_user_data_summary',
            });

            setUserDataSummary({
                ...summary,
                email,
                role: derivedRole,
            });
        } catch (err) {
            console.error('Error loading user data:', err);
            setError('Failed to load user data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportData = async () => {
        try {
            setIsExporting(true);
            setError(null);
            setSuccessMessage(null);

            const exportData = await callQuery({
                operation: 'export_user_data',
            });

            // Create a downloadable JSON file
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `capacity-health-user-data-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setSuccessMessage('Your data has been exported successfully.');
        } catch (err) {
            console.error('Error exporting user data:', err);
            setError('Failed to export user data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleDeleteData = async () => {
        if (deleteConfirmation.toLowerCase() !== 'delete my data') {
            setError('Please type "DELETE MY DATA" to confirm deletion.');
            return;
        }

        try {
            setIsDeleting(true);
            setError(null);
            setSuccessMessage(null);

            await callWrite({
                operation: 'delete_user_data',
            });

            setSuccessMessage('All your personal data has been deleted successfully.');
            setShowDeleteModal(false);
            setDeleteConfirmation('');

            // Reload the data summary to show zeros
            await loadUserData();
        } catch (err) {
            console.error('Error deleting user data:', err);
            setError('Failed to delete user data. Please try again or contact support.');
        } finally {
            setIsDeleting(false);
        }
    };

    const getTotalDataCount = () => {
        if (!userDataSummary) return 0;
        return (
            userDataSummary.notes_count +
            userDataSummary.chats_count +
            userDataSummary.favorites_count +
            userDataSummary.favorite_lists_count +
            userDataSummary.llm_feedback_count +
            userDataSummary.followed_patients_count
        );
    };

    return (
        <Container className="profile-page">
            <Row>
                <Col lg={8} className="mx-auto">
                    <h4 className="mt-4 mb-4">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        My Profile
                    </h4>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Account Information */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Account Information</h5>
                        </Card.Header>
                        <Card.Body>
                            {isLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">Loading...</span>
                                </div>
                            ) : (
                                <ListGroup variant="flush">
                                    <ListGroup.Item>
                                        <strong>Email:</strong> {email}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>Role:</strong>{' '}
                                        <Badge bg={derivedRole === 'ADMIN' ? 'danger' : derivedRole === 'BETA' ? 'warning' : 'secondary'}>
                                            {derivedRole}
                                        </Badge>
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>Username:</strong> {userDataSummary?.username || 'N/A'}
                                    </ListGroup.Item>
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Data Summary */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Your Data Summary</h5>
                        </Card.Header>
                        <Card.Body>
                            {isLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">Loading...</span>
                                </div>
                            ) : (
                                <>
                                    <Alert variant="info" className="mb-3">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        This shows all personal data we store about you in the Capacity Health system.
                                    </Alert>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>Clinical Notes</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.notes_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>AI Chat Conversations</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.chats_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>Favorite Patients</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.favorites_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>Favorite Lists</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.favorite_lists_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>Followed Patients</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.followed_patients_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <span>LLM Feedback Submissions</span>
                                            <Badge bg="primary" pill>
                                                {userDataSummary?.llm_feedback_count || 0}
                                            </Badge>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                            <strong>Total Data Points</strong>
                                            <Badge bg="dark" pill>
                                                {getTotalDataCount()}
                                            </Badge>
                                        </ListGroup.Item>
                                    </ListGroup>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Release Notes */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faInfoCircle} /> Release Notes
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted mb-3">
                                Stay up to date with the latest features, improvements, and bug fixes.
                            </p>
                            <Link to="/release-notes" className="btn btn-primary">
                                View Release Notes
                            </Link>
                        </Card.Body>
                    </Card>

                    {/* Data Management Actions */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Data Management</h5>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted mb-3">
                                You have the right to access, export, and delete your personal information stored in our system.
                            </p>

                            <div className="d-grid gap-3">
                                <Button
                                    variant="primary"
                                    onClick={handleExportData}
                                    disabled={isExporting || isLoading}
                                >
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    {isExporting ? 'Exporting...' : 'Export My Data (JSON)'}
                                </Button>

                                <Button
                                    variant="danger"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={isDeleting || isLoading}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    Delete All My Data
                                </Button>
                            </div>

                            <Alert variant="warning" className="mt-3 mb-0">
                                <strong>Note:</strong> Deleting your data will remove all notes, favorites, chat history, and preferences.
                                Your account will remain active, but all your content will be permanently deleted.
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Data Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <strong>Warning:</strong> This action cannot be undone!
                    </Alert>
                    <p>
                        You are about to permanently delete all your personal data from Capacity Health, including:
                    </p>
                    <ul>
                        <li>All clinical notes ({userDataSummary?.notes_count || 0})</li>
                        <li>All AI chat conversations ({userDataSummary?.chats_count || 0})</li>
                        <li>All favorites and favorite lists ({userDataSummary?.favorites_count || 0})</li>
                        <li>All followed patients ({userDataSummary?.followed_patients_count || 0})</li>
                        <li>All feedback submissions ({userDataSummary?.llm_feedback_count || 0})</li>
                    </ul>
                    <p>
                        Your account will remain active, but all your content will be gone forever.
                    </p>
                    <p className="mb-2">
                        <strong>To confirm, please type:</strong> <code>DELETE MY DATA</code>
                    </p>
                    <input
                        type="text"
                        className="form-control"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="Type here to confirm"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteData}
                        disabled={isDeleting || deleteConfirmation.toLowerCase() !== 'delete my data'}
                    >
                        {isDeleting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                Delete My Data
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};
